import { TourxLogo } from '../components/TourxLogo';

export function SupportPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <TourxLogo height={32} />
          </a>
          <a
            href="/login"
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Sign In
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Support</h1>
        <p className="text-gray-600 mb-10">
          Need help with TOURX? We&apos;re here to assist transport managers and drivers get the most from the platform.
        </p>

        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Email Support</h3>
            <p className="text-gray-600 text-sm mb-3">For general enquiries, technical issues, and account help.</p>
            <a href="mailto:support@tourx.co.uk" className="text-blue-600 font-medium hover:underline">support@tourx.co.uk</a>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Phone Support</h3>
            <p className="text-gray-600 text-sm mb-3">Available Monday to Friday, 8am – 6pm GMT.</p>
            <a href="tel:+441onal" className="text-green-600 font-medium hover:underline">Contact us for phone support</a>
          </div>
        </div>

        {/* FAQ Section */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>

        <div className="space-y-4 mb-12">
          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              How do I download tachograph data?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              Log in to the TOURX portal and navigate to the Tacho section. Select the driver and date range, then click Download. Files are provided in DDD and PDF format. You can also upload .ddd files directly from your tachograph download tool.
            </div>
          </details>

          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              How do I set up the TOURX mobile app?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              Download TOURX from the App Store. Your transport manager will provide you with login credentials. Open the app, enter your email and temporary password, then set up your 4-digit PIN for quick access. The app works offline and will sync data automatically when you&apos;re back online.
            </div>
          </details>

          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              How do walkaround checks work?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              Open the TOURX app, tap Walkaround Check, and select your vehicle. The app guides you through a DVSA-compliant 50-point checklist. You can attach photos to any defect found. Once submitted, your transport manager is notified immediately of any issues. All checks are stored for compliance auditing.
            </div>
          </details>

          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              I&apos;ve forgotten my password — how do I reset it?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              Contact your transport manager or admin. They can reset your password from the Users page in the TOURX portal. You&apos;ll receive a temporary password by email which you can change after logging in.
            </div>
          </details>

          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              What is the DCRS (Driver Compliance Rating System)?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              DCRS is TOURX&apos;s points-based compliance rating system. Infringements are assigned severity points, and drivers are placed on levels 1–6. Each level triggers a specific procedure (verbal warning through to disciplinary). Maintaining 3 months with no infringements reduces your level. This helps operators meet their DVSA earned recognition obligations.
            </div>
          </details>

          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              Can I use the app offline?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              Yes. The TOURX mobile app is designed for drivers on the road. Walkaround checks, POD signatures, and other forms can be completed without an internet connection. Data is stored securely on your device and syncs automatically when connectivity is restored.
            </div>
          </details>

          <details className="bg-white rounded-xl border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer text-gray-900 font-medium hover:bg-gray-50 transition-colors">
              How do I report a bug or request a feature?
            </summary>
            <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">
              Email <a href="mailto:support@tourx.co.uk" className="text-blue-600 hover:underline">support@tourx.co.uk</a> with
              a description of the issue or feature request. Please include screenshots if possible. We review all feedback and prioritise based on impact.
            </div>
          </details>
        </div>

        {/* App info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-12">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">System Requirements</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p className="font-medium text-gray-900 mb-1">TOURX Portal (Web)</p>
              <p>Modern web browser (Chrome, Safari, Edge, Firefox)</p>
              <p>Internet connection required</p>
            </div>
            <div>
              <p className="font-medium text-gray-900 mb-1">TOURX Mobile App (iOS)</p>
              <p>iOS 15.0 or later</p>
              <p>Camera access for walkaround photo evidence</p>
              <p>Location services for tracking (optional)</p>
            </div>
          </div>
        </div>

        {/* Links */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
          <a href="/privacy" className="hover:text-gray-700 transition-colors">Privacy Policy</a>
          <span className="text-gray-300">|</span>
          <a href="/marketing" className="hover:text-gray-700 transition-colors">About TOURX</a>
          <span className="text-gray-300">|</span>
          <a href="mailto:support@tourx.co.uk" className="hover:text-gray-700 transition-colors">support@tourx.co.uk</a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Craner Kirkman Ltd. All rights reserved. TOURX is a registered trademark.
        </div>
      </footer>
    </div>
  );
}
